import moment from 'moment'

/**
 * Low-value PEP credential-pool infrastructure chatter (loading / loaded the shared pool) repeats
 * many times per session and tells a CSM nothing about the hotel. We hide it from the default
 * timeline so sign-in, data-pull, and recovery events stay readable. Only Info-level pool noise is
 * filtered; warnings and errors are always kept.
 */
export function isLowValueInfraEvent(ev) {
  if (!ev) {
    return false
  }
  const type = (ev.eventType ?? ev.EventType ?? '').toString().trim()
  if (type !== 'PepCredential') {
    return false
  }
  const level = (ev.severity ?? ev.Severity ?? '').toString().toLowerCase()
  if (level !== '' && level !== 'info' && level !== 'information') {
    return false
  }
  const msg = (ev.message ?? ev.Message ?? '').toString().toLowerCase()
  return msg.includes('loading pool') || msg.includes('pool loaded')
}

function eventTimeValue(ev) {
  const t = moment(ev.occurredAt ?? ev.OccurredAt)
  return t.isValid() ? t.valueOf() : null
}

/**
 * Collapses repeated timeline rows that share the same signature (event type + code + message)
 * into a single representative row carrying an occurrence count and the first/last timestamps.
 * Input rows are expected newest-first; the representative keeps the newest occurrence so the
 * displayed time still reflects the most recent event. Pure; safe to unit test without rendering.
 *
 * @param {Array<{kind: string, data: object}>} rows
 * @param {(ev: object) => string} signatureOf
 * @returns {Array<{kind: string, data: object, count: number, firstAt: string|null, lastAt: string|null}>}
 */
export function groupTelemetryRows(rows, signatureOf) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return []
  }
  const bySignature = new Map()
  const order = []
  for (const row of rows) {
    const ev = row?.data
    if (!ev) {
      continue
    }
    const sig = signatureOf(ev)
    const tv = eventTimeValue(ev)
    if (!bySignature.has(sig)) {
      bySignature.set(sig, {
        ...row,
        count: 1,
        minTime: tv,
        maxTime: tv
      })
      order.push(sig)
      continue
    }
    const group = bySignature.get(sig)
    group.count += 1
    if (tv != null) {
      group.minTime = group.minTime == null ? tv : Math.min(group.minTime, tv)
      group.maxTime = group.maxTime == null ? tv : Math.max(group.maxTime, tv)
    }
  }
  return order.map(sig => {
    const group = bySignature.get(sig)
    const { minTime, maxTime, ...rest } = group
    return {
      ...rest,
      firstAt: minTime != null ? moment(minTime).toISOString() : null,
      lastAt: maxTime != null ? moment(maxTime).toISOString() : null
    }
  })
}
